import { FC, useState } from 'react';
import classNames from 'classnames';
import { EmojiPicker } from 'elements/EmojiPicker';
import ClickAwayListener from 'react-click-away-listener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Box } from 'models/Box';
import BoxIcon from 'assets/icons/icon-box.svg';
import Button from 'elements/Button';
import { closeModal } from 'xstate/actors/ModalActor';
import { useUpdateBoxAppearance } from 'networking/mindfeed/hooks/useUpdateBoxAppearance';
import { contrastingForeground } from 'utils/ColorUtils';
import { useTranslations } from 'next-intl';

// Background-colour swatches (mirrors the mobile palette). `null` is the
// "no colour" option, shown first as a white swatch.
const TILE_COLORS = [
  '#69A8FF',
  '#62F0C5',
  '#FFF79D',
  '#FFD998',
  '#FFD6D1',
  '#FF827A',
  '#FC9CD4',
  '#C5A4F0',
  '#F5F3C1',
];
const SWATCHES: (string | null)[] = [null, ...TILE_COLORS];

interface Props {
  box: Box;
}

const BoxAppearanceForm: FC<Props> = ({ box }) => {
  const t = useTranslations();
  const [emoji, setEmoji] = useState<string | null>(
    box.display?.tile.emoji ?? null,
  );
  const [color, setColor] = useState<string | null>(
    box.display?.tile.color ?? null,
  );
  const [isPickerVisible, setPickerVisible] = useState(false);

  const updateAppearance = useUpdateBoxAppearance({ onSuccess: closeModal });

  const onSave = () => {
    updateAppearance.mutate({ boxId: box.id, color, emoji });
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>{t('boxAppearance.description')}</p>

      {/* Emoji */}
      <div className={styles.emoji.wrap}>
        <button
          type="button"
          className={styles.emoji.circle}
          style={{ backgroundColor: color ?? '#F3F4F6' }}
          onClick={() => setPickerVisible((visible) => !visible)}
        >
          {emoji ? (
            <span className={styles.emoji.value}>{emoji}</span>
          ) : (
            <BoxIcon
              className={styles.emoji.placeholder}
              style={{ color: contrastingForeground(color) }}
            />
          )}
        </button>

        <button
          type="button"
          className={emoji ? styles.emoji.remove : styles.emoji.add}
          onClick={() => {
            if (emoji) {
              setEmoji(null);
            } else {
              setPickerVisible(true);
            }
          }}
        >
          {emoji ? t('common.removeEmoji') : t('common.addEmoji')}
        </button>

        {isPickerVisible && (
          <ClickAwayListener onClickAway={() => setPickerVisible(false)}>
            <div className={styles.emoji.picker}>
              <EmojiPicker
                onEmojiClick={(emojiObject) => {
                  setEmoji(emojiObject.emoji);
                  setPickerVisible(false);
                }}
              />
            </div>
          </ClickAwayListener>
        )}
      </div>

      {/* Background colour */}
      <div>
        <h3 className={styles.colors.label}>
          {t('boxAppearance.backgroundColour')}
        </h3>
        <div className={styles.colors.grid}>
          {SWATCHES.map((swatch) => {
            const isSelected = color === swatch;
            return (
              <button
                key={swatch ?? 'none'}
                type="button"
                aria-label={swatch ?? t('boxAppearance.noColour')}
                aria-pressed={isSelected}
                onClick={() => setColor(swatch)}
                className={classNames(
                  styles.colors.swatch,
                  swatch === null && styles.colors.none,
                )}
                style={swatch ? { backgroundColor: swatch } : undefined}
              >
                {isSelected && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={styles.colors.check}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Button
          type="button"
          variant="solid"
          color="neutral"
          size="sm"
          onClick={closeModal}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          variant="solid"
          color="primary"
          size="sm"
          onClick={onSave}
          disabled={updateAppearance.isPending}
          loading={updateAppearance.isPending}
          loadingText={t('common.saving')}
          className="bg-mf-blue! hover:bg-mf-blue-600!"
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
};

export default BoxAppearanceForm;

const styles = {
  container: 'px-6 pb-6 space-y-6',
  description: 'text-sm text-gray-500',
  emoji: {
    wrap: 'relative flex flex-col items-center gap-2',
    circle:
      'flex items-center justify-center w-20 h-20 rounded-full transition-transform hover:scale-105 focus:outline-none',
    value: 'text-3xl leading-none',
    placeholder: 'w-8 h-8',
    add: 'text-sm text-gray-500 hover:text-gray-700',
    remove: 'text-sm text-mf-red-600 hover:text-mf-red-700',
    picker: 'absolute z-20 top-full mt-2',
  },
  colors: {
    label: 'mb-3 text-[15px] font-bold text-mf-off-black-950',
    grid: 'grid grid-cols-5 gap-3 max-w-xs',
    swatch:
      'flex items-center justify-center w-10 h-10 rounded-full focus:outline-none',
    none: 'bg-white border border-gray-300',
    check: 'text-slate-800 text-sm',
  },
  footer: 'flex justify-end gap-3',
};
